import { useCallback, useEffect, useState } from "react";
import { Form, Button, Spinner } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { updatePassword, signInWithEmailAndPassword } from "firebase/auth";
import { auth } from "../../lib/config/firebaseInit";
import { useUserContext } from "../../context/UserContext";
import { toast } from "../../utilities/toast";
import {
  ArrowLeft,
  Eye,
  EyeSlash,
  CheckCircleFill,
  PersonCircle,
  ShieldLock,
} from "react-bootstrap-icons";
import { Seo } from "../../components/Seo";
import { generateNoIndexSEO } from "../../lib/seo/metaGenerators";

// ─── Constants ────────────────────────────────────────────────────────────────

const PASSWORD_RULES = {
  MIN_LENGTH: 6,
  MAX_LENGTH: 16,
  SPECIAL_CHARS: /[!@#$%^&*(),.?":{}|<>]/,
  UPPERCASE: /[A-Z]/,
  LOWERCASE: /[a-z]/,
  NUMBER: /\d/,
};

const RULE_DEFS = [
  { key: "minLength",      label: `${PASSWORD_RULES.MIN_LENGTH}+ characters` },
  { key: "maxLength",      label: `Max ${PASSWORD_RULES.MAX_LENGTH} chars`   },
  { key: "hasNumber",      label: "Number"                                   },
  { key: "hasSpecialChar", label: "Special character"                        },
  { key: "hasUpperCase",   label: "Uppercase letter"                         },
  { key: "hasLowerCase",   label: "Lowercase letter"                         },
  { key: "passwordsMatch", label: "Passwords match"                          },
];

// ─── Shared styles ────────────────────────────────────────────────────────────

const pageWrap = {
  minHeight: "100vh",
  // backgroundColor: "#f2f2f7",
  padding: "24px 16px 48px",
};

const card = {
  backgroundColor: "#fff",
  borderRadius: 18,
  border: "1px solid rgba(15,23,42,0.07)",
  padding: "24px",
  boxShadow: "0 2px 12px rgba(15,23,42,0.06)",
};

const labelStyle = {
  fontSize: "0.72rem",
  fontWeight: 600,
  color: "rgba(60,60,67,0.55)",
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  display: "block",
  marginBottom: 6,
};

const inputStyle = {
  backgroundColor: "rgba(118,118,128,0.1)",
  border: "1px solid transparent",
  borderRadius: 10,
  padding: "11px 14px",
  fontSize: "0.9375rem",
  color: "#1c1c1e",
  width: "100%",
};

const primaryBtn = {
  width: "100%",
  borderRadius: 12,
  padding: "13px",
  fontSize: "0.9375rem",
  fontWeight: 600,
  border: "none",
};

// ─── Password rules grid ──────────────────────────────────────────────────────

const PasswordRulesList = ({ passwordRules }) => (
  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "7px 10px", marginBottom: 20 }}>
    {RULE_DEFS.map(({ key, label }) => {
      const met = passwordRules[key];
      return (
        <div key={key} style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <div style={{
            width: 16, height: 16, borderRadius: "50%", flexShrink: 0,
            backgroundColor: met ? "rgba(52,199,89,0.14)" : "rgba(60,60,67,0.08)",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            {met
              ? <CheckCircleFill size={10} color="#34c759" />
              : <div style={{ width: 4, height: 4, borderRadius: "50%", backgroundColor: "rgba(60,60,67,0.3)" }} />
            }
          </div>
          <span style={{ fontSize: "0.78rem", color: met ? "#34c759" : "rgba(60,60,67,0.5)", fontWeight: met ? 500 : 400 }}>
            {label}
          </span>
        </div>
      );
    })}
  </div>
);

// ─── PasswordField ────────────────────────────────────────────────────────────

const PasswordField = ({ label, name, value, onChange, show, onToggle, placeholder }) => (
  <div style={{ marginBottom: 12 }}>
    <label style={labelStyle}>{label}</label>
    <div style={{ position: "relative" }}>
      <Form.Control
        type={show ? "text" : "password"}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        style={{ ...inputStyle, paddingRight: 44 }}
      />
      <button
        type="button"
        onClick={onToggle}
        style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", padding: 4, color: "rgba(60,60,67,0.4)", lineHeight: 1 }}
      >
        {show ? <EyeSlash size={16} /> : <Eye size={16} />}
      </button>
    </div>
  </div>
);

// ─── SecuritySettings ─────────────────────────────────────────────────────────

const SecuritySettings = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, isLoading } = useUserContext();

  const [formData, setFormData] = useState({ currentPassword: "", newPassword: "", confirmPassword: "" });
  const [passwordRules, setPasswordRules] = useState({
    minLength: false, maxLength: false, hasNumber: false,
    hasSpecialChar: false, hasUpperCase: false, hasLowerCase: false, passwordsMatch: false,
  });
  const [showPassword, setShowPassword] = useState({ current: false, new: false, confirm: false });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const { newPassword, confirmPassword } = formData;
    setPasswordRules({
      minLength:      newPassword.length >= PASSWORD_RULES.MIN_LENGTH,
      maxLength:      newPassword.length <= PASSWORD_RULES.MAX_LENGTH && newPassword.length > 0,
      hasNumber:      PASSWORD_RULES.NUMBER.test(newPassword),
      hasSpecialChar: PASSWORD_RULES.SPECIAL_CHARS.test(newPassword),
      hasUpperCase:   PASSWORD_RULES.UPPERCASE.test(newPassword),
      hasLowerCase:   PASSWORD_RULES.LOWERCASE.test(newPassword),
      passwordsMatch: newPassword === confirmPassword && newPassword !== "",
    });
  }, [formData.newPassword, formData.confirmPassword]);

  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData((s) => ({ ...s, [name]: value }));
  }, []);

  const toggleShow = (key) => setShowPassword((s) => ({ ...s, [key]: !s[key] }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.currentPassword) { toast.error("Enter your current password"); return; }
    setIsSubmitting(true);
    try {
      await signInWithEmailAndPassword(auth, user.email, formData.currentPassword);
      await updatePassword(auth.currentUser, formData.newPassword);
      toast.success("Password updated successfully");
      setFormData({ currentPassword: "", newPassword: "", confirmPassword: "" });
    } catch (err) {
      const messages = {
        "auth/wrong-password":        "Current password is incorrect",
        "auth/invalid-credential":    "Current password is incorrect",
        "auth/too-many-requests":     "Too many attempts. Try again later",
        "auth/network-request-failed": "Network error. Check your connection",
        "auth/requires-recent-login": "Please sign in again to continue",
      };
      toast.error(messages[err.code] ?? "Failed to update password");
    } finally {
      setIsSubmitting(false);
    }
  };

  const allRulesMet = Object.values(passwordRules).every(Boolean);

  // ── Loading ────────────────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <div style={{ ...pageWrap, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Spinner animation="border" variant="primary" role="status" style={{ width: "2.25rem", height: "2.25rem" }}>
          <span className="visually-hidden">Loading…</span>
        </Spinner>
      </div>
    );
  }

  // ── Unauthenticated ────────────────────────────────────────────────────────

  if (!isAuthenticated) {
    return (
      <div style={{ ...pageWrap, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ width: "100%", maxWidth: 380, textAlign: "center", padding: "0 16px" }}>
          <div style={{ width: 72, height: 72, borderRadius: "50%", backgroundColor: "rgba(0,122,255,0.1)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px" }}>
            <PersonCircle size={34} color="#007aff" />
          </div>
          <h2 style={{ fontSize: "1.375rem", fontWeight: 700, color: "#1c1c1e", letterSpacing: "-0.02em", marginBottom: 8 }}>
            Sign in to continue
          </h2>
          <p style={{ fontSize: "0.9375rem", color: "rgba(60,60,67,0.6)", maxWidth: 260, lineHeight: 1.45, margin: "0 auto 32px" }}>
            Sign in to manage your security settings
          </p>
          <button
            onClick={() => navigate("/login")}
            style={{ width: "100%", maxWidth: 280, padding: "13px 1.5rem", borderRadius: 12, border: "none", backgroundColor: "rgba(0,122,255,0.1)", color: "#007aff", fontSize: "0.9375rem", fontWeight: 600, cursor: "pointer", display: "block", margin: "0 auto" }}
          >
            Sign In
          </button>
        </div>
      </div>
    );
  }

  // ── Form ───────────────────────────────────────────────────────────────────

  return (
    <div style={pageWrap}>
      <Seo {...generateNoIndexSEO("Security Settings")} />
      <div style={{ width: "100%", maxWidth: 480, margin: "0 auto" }}>

        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
          <Button
            variant="light"
            onClick={() => navigate(-1)}
            className="rounded-circle p-0 d-flex align-items-center justify-content-center flex-shrink-0"
            style={{ width: 36, height: 36, backgroundColor: "rgba(60,60,67,0.08)", border: "none" }}
          >
            <ArrowLeft size={18} color="#1c1c1e" />
          </Button>
          <div>
            <h1 style={{ fontSize: "1.25rem", fontWeight: 700, color: "#1c1c1e", letterSpacing: "-0.02em", margin: 0 }}>
              Privacy & Security
            </h1>
            <p style={{ fontSize: "0.8125rem", color: "rgba(60,60,67,0.55)", margin: 0 }}>
              Update your password
            </p>
          </div>
        </div>

        {/* Card */}
        <div style={card}>

          {/* Email (readonly) */}
          <div style={{ marginBottom: 20 }}>
            <label style={labelStyle}>Account</label>
            <div style={{
              display: "flex", alignItems: "center", gap: 10,
              backgroundColor: "rgba(118,118,128,0.08)",
              borderRadius: 10, padding: "11px 14px",
            }}>
              <div style={{ width: 28, height: 28, borderRadius: "50%", backgroundColor: "rgba(0,122,255,0.12)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <ShieldLock size={13} color="#007aff" />
              </div>
              <span style={{ fontSize: "0.9375rem", color: "#1c1c1e", fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {user.email}
              </span>
            </div>
          </div>

          <Form onSubmit={handleSubmit}>
            <PasswordField
              label="Current Password"
              name="currentPassword"
              value={formData.currentPassword}
              onChange={handleInputChange}
              show={showPassword.current}
              onToggle={() => toggleShow("current")}
              placeholder="Enter current password"
            />
            <PasswordField
              label="New Password"
              name="newPassword"
              value={formData.newPassword}
              onChange={handleInputChange}
              show={showPassword.new}
              onToggle={() => toggleShow("new")}
              placeholder="Create new password"
            />
            <PasswordField
              label="Confirm New Password"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleInputChange}
              show={showPassword.confirm}
              onToggle={() => toggleShow("confirm")}
              placeholder="Repeat new password"
            />

            {formData.newPassword.length > 0 && (
              <div style={{ marginTop: 4 }}>
                <PasswordRulesList passwordRules={passwordRules} />
              </div>
            )}

            <Button
              type="submit"
              variant="primary"
              disabled={isSubmitting || !allRulesMet || !formData.currentPassword}
              style={primaryBtn}
            >
              {isSubmitting
                ? <><Spinner size="sm" animation="border" className="me-2" />Updating…</>
                : "Update Password"
              }
            </Button>
          </Form>

        </div>
      </div>
    </div>
  );
};

export default SecuritySettings;
